import { ReactComponent as LeftArrow } from 'icons/left-arrow.svg';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createNewVehicle, editVehicle, getVehicleById, getVehiclesByFilter } from 'features/vehicle/action';
import { useAppContext } from 'components/Context/AppContext';
import ContentLeft from './ContentDetailLeft';
import ContentRight from './ContentDetailRight';
import { deleteImageVehicle, resetSelectedVehicle } from 'features/vehicle/vehicleSlice';
import { getAllCategories } from 'features/category/actions';
import { useNavigate, useParams } from 'react-router-dom';
import { checkEmptyObject } from 'utils/functionality';
import { getAllBrands } from 'features/brands/actions';
import { getAllGarages } from 'features/garages/actions';
import { useFliptBoolean } from '@flipt-io/flipt-client-react';
import { trackEvent } from 'utils/analytics';

// initial data
const initialValue = {
  id: '',
  name: '',
  year: '',
  price: 0,
  slash_price: 0,
  price_with_driver: 0,
  license_number: '',
  max_passanger: '',
  min_suitcase: '',
  max_suitcase: '',
  disability_allowed: false,
  smoke_allowed: false,
  pet_allowed: false,
  status: 'available',
  support_driver: false,
  required_driver: false,
  transmission: 'manual',
  brand_vehicles: 0,
  garage_vehicles: 0,
  photo: [],
  category_vehicles: 0,
  support_charter: false,
  charter_pricing: [],
  price_charter_half: 0,
  price_charter_full: 0,
};

const VehicleDetailPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id: vehicleId } = useParams();

  const isAddVehicleState = location.pathname.includes('add-vehicle');

  // global state
  const {
    data: dataVehicle,
    selected: selectedVehicle,
    isLoading,
    isError,
    errorMessage,
  } = useSelector((state) => state.vehicle);
  const rentalLocation = useSelector((state) => state.rentalLocation?.selected);
  const garages = useSelector((state) => state.garages?.data);

  // context
  const { setShowConfirmation, showToast, setShowSpinner, setShowVehiclePriceConfirmation } = useAppContext();

  // data updated car
  const [dataCar, setDataCar] = useState(initialValue);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // driver availibility list
  const [withoutDriver, setWithoutDriver] = useState(false);
  const [withDriver, setWithDriver] = useState(false);
  const [carCharter, setCarCharter] = useState(false);

  const isCarCharterEnabled = useFliptBoolean('FLAG_CHARTER', false, 'anonymous');
  const isHideSlashPriceInput = useFliptBoolean('FLAG_HIDE_VEHICLE_SLASH_PRICE_INPUT', false, 'anonymous');
  const isVehicleUpdateByLocation = useFliptBoolean('FLAG_FE_VEHICLE_UPDATE_BY_LOCATION', false, 'anonymous');

  // destructured the data
  const {
    id,
    name,
    year,
    price,
    slash_price,
    price_with_driver,
    license_number,
    max_passanger,
    min_suitcase,
    max_suitcase,
    disability_allowed,
    smoke_allowed,
    pet_allowed,
    status,
    support_driver,
    required_driver,
    transmission,
    brand_vehicles,
    garage_vehicles,
    photo,
    category_vehicles,
    support_charter,
    price_charter_half,
    price_charter_full,
  } = dataCar;

  // delete image vehicle
  const handleRemoveImageVehicle = (image) => {
    const updatedPhotos = selectedVehicle.photo?.filter((item) => {
      if (typeof item === 'string') {
        return item !== image;
      } else {
        return item.name !== image;
      }
    });

    dispatch(deleteImageVehicle(updatedPhotos));
  };

  // function sendPayload action
  const sendPayloadData = async (
    action,
    payload,
    fulfilledMessage,
    errorMessage1,
    errorMessage2,
    getSameVehicleName,
  ) => {
    try {
      trackEvent(action === 'ADD' ? 'vehicle_created' : 'vehicle_updated', {
        vehicle_id: action === 'ADD' ? undefined : id,
        source: 'vehicle_detail',
      });
      await dispatch(action === 'ADD' ? createNewVehicle(payload) : editVehicle({ payload, id })).unwrap();
      setShowSpinner(true);

      if (action === 'UPDATE') {
        trackEvent('price_update_result', {
          scope: payload.update_matching_vehicle ? 'bulk' : 'single',
          result: 'success',
          affected_count: payload.update_matching_vehicle ? getSameVehicleName?.length : 1,
        });
      }

      showToast({ type: 'success', message: fulfilledMessage });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      trackEvent('price_update_result', {
        scope: payload.update_matching_vehicle ? 'bulk' : 'single',
        result: 'error',
        affected_count: payload.update_matching_vehicle ? getSameVehicleName?.length : 1,
      });
      showToast({ type: 'error', message: `${errorMessage2}: ${errorMessage1}-${error}` });
    } finally {
      navigate('/vehicles');
      setShowSpinner(false);
    }
  };

  const submitHandler = (e) => {
    e.preventDefault();

    // payload
    const payload = {
      name: name?.trim(),
      year: +year,
      price: +price,
      slash_price: isHideSlashPriceInput ? 0 : +slash_price,
      price_with_driver: +price_with_driver,
      license_number: license_number?.toLocaleUpperCase(),
      max_passanger: +max_passanger,
      min_suitcase: +min_suitcase,
      max_suitcase: +max_suitcase,
      disability_allowed,
      smoke_allowed,
      pet_allowed,
      status,
      support_driver,
      required_driver,
      transmission,
      brand_vehicles: +brand_vehicles,
      garage_vehicles: +garage_vehicles,
      photo: photo?.map((item) => (typeof item === 'string' ? item : item.name)),
      category_vehicles: +category_vehicles,
      ...(isCarCharterEnabled && {
        charter_pricing: carCharter
          ? [
              {
                price: +price_charter_half,
                duration: 4,
                label: 'Half Day',
                currency: rentalLocation?.currency || '',
              },
              {
                price: +price_charter_full,
                duration: 8,
                label: 'Full Day',
                currency: rentalLocation?.currency || '',
              },
            ]
          : undefined,
        support_charter,
      }),
    };

    // loop through payload to check if there were invalid form
    for (let prop in payload) {
      if (!payload[prop]) {
        if (prop === 'brand_vehicles') {
          return showToast({ type: 'error', message: 'Merk mobil tidak boleh kosong' });
        } else if (prop === 'name') {
          return showToast({ type: 'error', message: 'Nama mobil tidak boleh kosong' });
        } else if (prop === 'year') {
          return showToast({ type: 'error', message: 'Tahun mobil tidak boleh Kosong' });
        } else if (prop === 'category_vehicles') {
          return showToast({ type: 'error', message: 'Pilih Kategori Mobil' });
        } else if (prop === 'price') {
          if (withoutDriver) {
            return showToast({ type: 'error', message: 'Harga Mobil Tanpa Supir tidak boleh Kosong' });
          } else {
            continue;
          }
        } else if (prop === 'price_with_driver') {
          if (withDriver) {
            return showToast({ type: 'error', message: 'Harga Mobil Dengan Supir tidak boleh Kosong' });
          } else {
            continue;
          }
        } else if (prop === 'max_passanger') {
          return showToast({ type: 'error', message: 'Jumlah kursi tidak boleh Kosong' });
        } else if (prop === 'max_suitcase') {
          return showToast({ type: 'error', message: 'Maksimal koper tidak boleh Kosong' });
        } else if (prop === 'garage_vehicles') {
          return showToast({ type: 'error', message: 'Garasi tidak boleh Kosong' });
        }
      }
      if (prop === 'photo' && payload[prop].length === 0) {
        return showToast({ type: 'error', message: 'Foto Mobil tidak boleh kosong' });
      }
    }

    if (!withoutDriver && !withDriver && isCarCharterEnabled && !carCharter) {
      showToast({ type: 'error', message: 'Harga Mobil tidak boleh Kosong' });
      return;
    }

    if (isCarCharterEnabled) {
      if (carCharter) {
        const isPricingEmpty = payload.charter_pricing?.filter((item) => item.price === 0);
        if (isPricingEmpty?.length > 0)
          return showToast({ type: 'error', message: 'Harga Mobil Charter tidak boleh Kosong' });
      }
    }

    // check if license number is valid
    const regexLicenseNumber = /^([A-Za-z]{1,2})\s(\d+)\s(\w{1,3})$/;
    if (!new RegExp(regexLicenseNumber).exec(license_number)) {
      return showToast({ type: 'error', message: 'Plat nomor tidak valid' });
    }

    // Get all vehicle with same name and location
    const getSameVehicleName = dataVehicle?.vehicles?.filter(
      (item) => item.name?.toLowerCase() === payload.name.toLowerCase().trim(),
    );

    const sameUnitVehicle = getSameVehicleName?.length || 0;
    const isCurrentVehicleInList = getSameVehicleName?.some((data) => data.id === id);

    // if there are more than 1 vehicle with same name and location, show confirmation modal
    // check if the selected vehicle is not on the sameIdVehicleInList, add 1 to the confirmation modal checking
    const showPriceConfirmation = sameUnitVehicle + (isCurrentVehicleInList ? 0 : 1) > 1;

    // check if the inputted without driver price is the same as the existing without driver price
    const sameWithoutDriverPrice =
      payload.price === getSameVehicleName?.find((item) => item.price === payload.price)?.price;

    // check if the inputted with driver price is the same as the existing with driver price
    const sameWithDriverPrice =
      payload.price_with_driver ===
      getSameVehicleName?.find((item) => item.price_with_driver === payload.price_with_driver)?.price_with_driver;

    // check if the inputted charter full price driver is the same as the existing charter full price driver
    // const sameCharterFullPrice =
    //   payload.price_charter_full ===
    //   getSameVehicleName
    //     ?.find((item) => item.charter_pricing?.some((pricing) => pricing.duration === 8))
    //     ?.charter_pricing?.find((pricing) => pricing.duration === 8)?.price;

    // check if the inputted charter half price is the same as the existing charter half price
    // const sameCharterHalfPrice =
    //   payload.price_charter_half ===
    //   getSameVehicleName
    //     ?.find((item) => item.charter_pricing?.some((pricing) => pricing.duration === 4))
    //     ?.charter_pricing?.find((pricing) => pricing.duration === 4)?.price;

    if (
      showPriceConfirmation &&
      ((withDriver && !sameWithDriverPrice) || (withoutDriver && !sameWithoutDriverPrice))
      // || (isCarCharterEnabled && carCharter && (!sameCharterFullPrice || !sameCharterHalfPrice))
    ) {
      trackEvent('price_update_initiated', {
        has_same_unit_at_location: getSameVehicleName?.length > 0,
      });

      setShowVehiclePriceConfirmation({
        show: true,
        onConfirm: () => {
          // check if Add
          if (isAddVehicleState) {
            sendPayloadData(
              'ADD',
              { ...payload, update_matching_vehicle: true },
              'Berhasil Menambahkan Mobil',
              'Gagal Menambahkan Mobil',
              'Terjadi Kesalahan',
            );
            return;
          }

          if (isVehicleUpdateByLocation) {
            sendPayloadData(
              'UPDATE',
              { ...payload, update_matching_vehicle: true },
              'Berhasil Update Data Mobil',
              'Gagal Mengupdate Data Mobil',
              'Terjadi Kesalahan',
            );
            return;
          }

          setShowConfirmation({
            message: 'Apakah anda yakin ingin mengubah data Mobil',
            show: true,
            onClick: () => {
              sendPayloadData(
                'UPDATE',
                { ...payload, update_matching_vehicle: true },
                'Berhasil Update Data Mobil',
                'Gagal Mengupdate Data Mobil',
                'Terjadi Kesalahan',
              );
            },
          });
        },
        vehicleName: name,
        ...(isVehicleUpdateByLocation && {
          locationName: garages?.find((garage) => garage.id === garage_vehicles)?.name,
          onReject: () => {
            // check if Add
            if (isAddVehicleState) {
              sendPayloadData(
                'ADD',
                { ...payload, update_matching_vehicle: false },
                'Berhasil Menambahkan Mobil',
                'Gagal Menambahkan Mobil',
                'Terjadi Kesalahan',
              );
              return;
            }

            sendPayloadData(
              'UPDATE',
              { ...payload, update_matching_vehicle: false },
              'Berhasil Update Data Mobil',
              'Gagal Mengupdate Data Mobil',
              'Terjadi Kesalahan',
            );
          },
        }),
      });
    } else {
      // check if Add
      if (isAddVehicleState) {
        sendPayloadData(
          'ADD',
          { ...payload, update_matching_vehicle: false },
          'Berhasil Menambahkan Mobil',
          'Gagal Menambahkan Mobil',
          'Terjadi Kesalahan',
        );
        return;
      }

      setShowConfirmation({
        message: 'Apakah anda yakin ingin mengubah data Mobil',
        show: true,
        onClick: () => {
          trackEvent('price_scope_decided', {
            scope: 'direct',
          });
          sendPayloadData(
            'UPDATE',
            { ...payload, update_matching_vehicle: false },
            'Berhasil Update Data Mobil',
            'Gagal Mengupdate Data Mobil',
            'Terjadi Kesalahan',
          );
        },
      });
    }
  };

  const getCharterPricing = (type) =>
    selectedVehicle?.charter_pricing?.find((item) => item.label.toLowerCase().includes(type))?.price;

  const getLocationIdByGarage = (garageId) => garages?.find((garage) => garage.id === garageId)?.location_id;

  // Spinner show
  useEffect(() => {
    if (isLoading) {
      setShowSpinner(true);
    } else setShowSpinner(false);
  }, [isLoading]);

  // Error Handling
  useEffect(() => {
    if (!isError) return;

    if (Object.hasOwn(errorMessage, 'slug')) {
      showToast({ type: 'error', message: errorMessage.slug });
    } else {
      showToast({ type: 'error', message: 'Terjadi Kesalahan' });
    }
  }, [isError, errorMessage]);

  // set data car with selected vehicle when showVehicleDetail true
  useEffect(() => {
    if (!isAddVehicleState && !checkEmptyObject(selectedVehicle)) {
      setDataCar({
        ...selectedVehicle,
        category_vehicles: selectedVehicle.category?.id,
        ...(isCarCharterEnabled && {
          price_charter_half: getCharterPricing('half'),
          price_charter_full: getCharterPricing('full'),
        }),
      });
      return;
    }

    setDataCar((prev) => ({ ...prev, photo: selectedVehicle?.photo === undefined ? [] : selectedVehicle?.photo }));
  }, [selectedVehicle, isCarCharterEnabled]);

  // reset selected vehicle when showVehicledetail false
  useEffect(() => {
    if (isAddVehicleState && !checkEmptyObject(selectedVehicle)) {
      dispatch(resetSelectedVehicle());
    }
  }, []);

  // set inital value driver availibility
  useEffect(() => {
    if (!isFirstLoad) return;

    if (isAddVehicleState) return;

    if (isCarCharterEnabled && support_charter) {
      setCarCharter(true);
      setWithDriver(false);
      setWithoutDriver(false);
      return;
    }

    if (support_driver && required_driver) {
      setWithDriver(true);
      setWithoutDriver(false);
      return;
    }

    if (!support_driver && !required_driver) {
      setWithDriver(false);
      setWithoutDriver(true);
      return;
    }

    if (support_driver && !required_driver) {
      setWithDriver(true);
      setWithoutDriver(true);
    }
  }, [support_driver, required_driver, support_charter, isCarCharterEnabled]);

  // get all necessary data
  useEffect(() => {
    if (!isAddVehicleState) {
      dispatch(getVehicleById(vehicleId));
    }
    dispatch(getAllBrands());
    dispatch(getAllGarages());
    dispatch(getAllCategories({ page: 1 }));
  }, []);

  useEffect(() => {
    if (checkEmptyObject(selectedVehicle) || isAddVehicleState) return;

    dispatch(
      getVehiclesByFilter({
        page: 0,
        limit: 0,
        locationRental: getLocationIdByGarage(selectedVehicle.garage_vehicles),
        name: selectedVehicle.name,
      }),
    );
  }, [selectedVehicle]);

  return (
    <div className="vehicle-detail__component">
      <button
        className="vehicle-detail__header"
        onClick={() => navigate(-1)}
        style={{ border: 'none', background: 'transparent' }}
      >
        <LeftArrow />
        <p>Kembali</p>
      </button>
      <form className="vehicle-detail__content" onSubmit={submitHandler}>
        <ContentLeft
          dataCar={dataCar}
          setDataCar={setDataCar}
          isFirstLoad={isFirstLoad}
          setIsFirstLoad={setIsFirstLoad}
          withoutDriver={withoutDriver}
          setWithoutDriver={setWithoutDriver}
          withDriver={withDriver}
          setWithDriver={setWithDriver}
          carCharter={carCharter}
          setCarCharter={setCarCharter}
        />
        <ContentRight imageFiles={photo} handleRemove={handleRemoveImageVehicle} vehicle={selectedVehicle} />
      </form>
    </div>
  );
};

export default VehicleDetailPage;
